import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDateFromISOString } from '../../utils/dateUtils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend as RechartLegend,
  ResponsiveContainer,
} from 'recharts';
const Legend = RechartLegend as any;
import { DatosConsumo } from '../../services/gestionService';

interface GraficoConsumoProps {
  datos: DatosConsumo[];
  isLoading: boolean;
}

interface DatosGrafico {
  fecha: string;
  [key: string]: string | number;
}

export const GraficoConsumo: React.FC<GraficoConsumoProps> = ({
  datos,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [mesActual, setMesActual] = useState(new Date());
  const [datosGrafico, setDatosGrafico] = useState<DatosGrafico[]>([]);
  const [insumos, setInsumos] = useState<string[]>([]);
  const [insumoSeleccionado, setInsumoSeleccionado] = useState<string | 'todos'>('todos');

  useEffect(() => {
    if (datos && datos.length > 0) {
      // Agrupar datos por fecha
      const datosAgrupados: { [key: string]: DatosGrafico } = {};

      datos.forEach((item) => {
        if (!datosAgrupados[item.fecha]) {
          datosAgrupados[item.fecha] = { fecha: item.fecha };
        }
        datosAgrupados[item.fecha][item.insumo_descripcion] =
          item.cantidad_consumida;
      });

      // Obtener lista única de insumos
      const insumosUnicos = Array.from(
        new Set(datos.map((d) => d.insumo_descripcion))
      ).sort();
      setInsumos(insumosUnicos);

      // Convertir a array ordenado por fecha
      const datosOrdenados = Object.values(datosAgrupados).sort(
        (a, b) =>
          createDateFromISOString(a.fecha as string).getTime() -
          createDateFromISOString(b.fecha as string).getTime()
      );
      setDatosGrafico(datosOrdenados);

      // Establecer el mes del primer dato si existe
      if (datos.length > 0) {
        const [year, month, day] = datos[0].fecha.split('-').map(Number);
        setMesActual(new Date(year, month - 1, day));
      }
    }
  }, [datos]);

  const handleMesAnterior = () => {
    setMesActual(
      new Date(mesActual.getFullYear(), mesActual.getMonth() - 1)
    );
  };

  const handleMesSiguiente = () => {
    setMesActual(
      new Date(mesActual.getFullYear(), mesActual.getMonth() + 1)
    );
  };

  // Filtrar datos del mes actual para la línea temporal
  const datosDelMes = datosGrafico.filter((d) => {
    const fecha = createDateFromISOString(d.fecha as string);
    return (
      fecha.getFullYear() === mesActual.getFullYear() &&
      fecha.getMonth() === mesActual.getMonth()
    );
  });

  const colores = [
    '#3b82f6', // blue
    '#ef4444', // red
    '#10b981', // green
    '#f59e0b', // amber
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (datosGrafico.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-50 rounded-lg">
        <div className="text-gray-400">{t('common.no_data')}</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minWidth: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* Navegador de mes y selector de insumo */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button
            onClick={handleMesAnterior}
            style={{ padding: '0.5rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
            title="Mes anterior"
          >
            <svg
              style={{ width: '1.25rem', height: '1.25rem', color: '#4b5563' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', minWidth: '200px', textAlign: 'center' }}>
            {mesActual.toLocaleDateString('es-CL', {
              month: 'long',
              year: 'numeric',
            })}
          </div>

          <button
            onClick={handleMesSiguiente}
            style={{ padding: '0.5rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
            title="Mes siguiente"
          >
            <svg
              style={{ width: '1.25rem', height: '1.25rem', color: '#4b5563' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Selector de insumo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
            {t('management.fuel')}:
          </label>
          <select
            value={insumoSeleccionado}
            onChange={(e) => setInsumoSeleccionado(e.target.value as string | 'todos')}
            style={{
              padding: '0.5rem 0.75rem',
              borderRadius: '0.375rem',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              color: '#111827',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <option value="todos">{t('common.all')}</option>
            {insumos.map((insumo) => (
              <option key={insumo} value={insumo}>
                {insumo}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Gráfico de Línea Temporal */}
      <div className="bg-white rounded-lg shadow p-6" style={{ width: '100%', height: '500px', minHeight: '500px', maxHeight: '500px', flexShrink: 0, display: 'block' }}>
        {datosDelMes.length > 0 ? (
          <ResponsiveContainer width="100%" height={450}>
            <LineChart
              data={datosDelMes}
              margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="fecha"
                tick={{ fontSize: 12 }}
                stroke="#6b7280"
              />
              <YAxis
                label={{
                  value: 'Cantidad',
                  angle: -90,
                  position: 'insideLeft',
                }}
                stroke="#6b7280"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
                verticalAlign="bottom"
                height={36}
              />
              {(insumoSeleccionado === 'todos' ? insumos : [insumoSeleccionado]).map((insumo) => {
                const indexOriginal = insumos.indexOf(insumo);
                return (
                  <Line
                    key={insumo}
                    type="monotone"
                    dataKey={insumo}
                    stroke={colores[indexOriginal % colores.length]}
                    strokeWidth={2}
                    dot={{ fill: colores[indexOriginal % colores.length], r: 4 }}
                    activeDot={{ r: 6 }}
                    name={insumo}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af' }}>
            {t('common.no_data')}
          </div>
        )}
      </div>
    </div>
  );
};
